import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient, type AlbumListItem, type AlbumDetail, type PaginatedResponse } from '@/lib/api';
import type {
  CreateAlbumRequest,
  UpdateAlbumRequest,
  AddPhotosToAlbumRequest,
  ReorderAlbumPhotosRequest,
} from '@omokage/shared';

// Query keys for albums
export const albumKeys = {
  all: ['albums'] as const,
  lists: () => [...albumKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...albumKeys.lists(), filters] as const,
  details: () => [...albumKeys.all, 'detail'] as const,
  detail: (id: string) => [...albumKeys.details(), id] as const,
};

// Album list response type
type AlbumListResponse = PaginatedResponse<AlbumListItem>;

// Album detail response type
interface AlbumDetailResponse extends AlbumDetail {
  meta: {
    count: number;
    hasMore: boolean;
    cursor?: string;
  };
}

/**
 * Hook to fetch paginated list of albums
 */
export function useAlbums(limit = 20) {
  return useInfiniteQuery<AlbumListResponse>({
    queryKey: albumKeys.list({ limit }),
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = { limit };
      if (pageParam) {
        params.cursor = pageParam as string;
      }
      return apiClient.get<AlbumListResponse>('/albums', { params });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const cursor = lastPage.meta.cursor;
      return typeof cursor === 'string' ? cursor : undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single album with its photos
 */
export function useAlbum(albumId: string, photoLimit = 50) {
  return useQuery<AlbumDetailResponse>({
    queryKey: albumKeys.detail(albumId),
    queryFn: () =>
      apiClient.get<AlbumDetailResponse>(`/albums/${albumId}`, {
        params: { limit: photoLimit },
      }),
    enabled: !!albumId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch more photos from an album
 */
export function useAlbumPhotos(albumId: string, limit = 50) {
  return useInfiniteQuery<AlbumDetailResponse>({
    queryKey: [...albumKeys.detail(albumId), 'photos'],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = { limit };
      if (pageParam) {
        params.cursor = pageParam as string;
      }
      return apiClient.get<AlbumDetailResponse>(`/albums/${albumId}`, { params });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const cursor = lastPage.meta.cursor;
      return typeof cursor === 'string' ? cursor : undefined;
    },
    enabled: !!albumId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new album
 */
export function useCreateAlbum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAlbumRequest) => apiClient.post<AlbumListItem>('/albums', data),
    onSuccess: () => {
      // Invalidate album list to refetch
      queryClient.invalidateQueries({ queryKey: albumKeys.lists() });
    },
  });
}

/**
 * Hook to update an album
 */
export function useUpdateAlbum(albumId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAlbumRequest) =>
      apiClient.put<AlbumListItem>(`/albums/${albumId}`, data),
    onSuccess: (updatedAlbum) => {
      // Update the album in cache
      queryClient.setQueryData(
        albumKeys.detail(albumId),
        (old: AlbumDetailResponse | undefined) => {
          if (!old) return old;
          return { ...old, ...updatedAlbum };
        }
      );
      // Invalidate list to update cover photo etc.
      queryClient.invalidateQueries({ queryKey: albumKeys.lists() });
    },
  });
}

/**
 * Hook to delete an album
 */
export function useDeleteAlbum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (albumId: string) =>
      apiClient.delete<{ success: true; message: string }>(`/albums/${albumId}`),
    onSuccess: (_, albumId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: albumKeys.detail(albumId) });
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: albumKeys.lists() });
    },
  });
}

/**
 * Hook to add photos to an album
 */
export function useAddPhotosToAlbum(albumId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddPhotosToAlbumRequest) =>
      apiClient.post<{ success: true; message: string; addedCount: number; skippedCount: number }>(
        `/albums/${albumId}/photos`,
        data
      ),
    onSuccess: () => {
      // Invalidate album detail to refetch photos
      queryClient.invalidateQueries({ queryKey: albumKeys.detail(albumId) });
      // Invalidate list to update photo count
      queryClient.invalidateQueries({ queryKey: albumKeys.lists() });
    },
  });
}

/**
 * Hook to remove a photo from an album
 */
export function useRemovePhotoFromAlbum(albumId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (photoId: string) =>
      apiClient.delete<{ success: true; message: string }>(`/albums/${albumId}/photos/${photoId}`),
    onSuccess: () => {
      // Invalidate album detail to refetch photos
      queryClient.invalidateQueries({ queryKey: albumKeys.detail(albumId) });
      // Invalidate list to update photo count
      queryClient.invalidateQueries({ queryKey: albumKeys.lists() });
    },
  });
}

/**
 * Hook to reorder photos in an album
 */
export function useReorderAlbumPhotos(albumId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderAlbumPhotosRequest) =>
      apiClient.put<{ success: true; message: string }>(`/albums/${albumId}/photos/reorder`, data),
    onSuccess: () => {
      // Invalidate album detail to refetch photos with new order
      queryClient.invalidateQueries({ queryKey: albumKeys.detail(albumId) });
    },
  });
}
